/* eslint-disable max-len */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import ProgressButton from "react-progress-button";
import { isEmpty } from "@utils/lodash";

import {
	ADDITIONAL_JOB_TYPES,
	JOB_PAYMENT_TYPES,
	CLIENTS_PAYS_FOR_THE_JOB_OPTIONS,
	ELECTRONICALLY_BILLED_OPTIONS,
} from "@constants";
import { JobService } from "@services";
import utils from "@utils";

import { Control, Option } from "../Control/Control";
import ReactSelect from "../ReactSelect/ReactSelect";
import Modal from "../Modal/Modal";
import Loader from "../Loader/Loader";

import "./SchedulingOnlyOptionsModal.scss";

const defaultOptions = {
	SchedulingJobType: null,
	AdditionalInformation: "",
	PaymentDetails: "",
	PaymentMethod: null,
	SchedulingBillingContactId: "",
	NotInvoicingInformation: "",
};

const defaultScheduleOnlyOptions = {
	ClientPaysForTheJob: null,
	ElectronicallyBilled: null,
	...defaultOptions,
};

const defaultValidations = {
	clientWillNotBePaying: false,
	clientWillNotBeBilled: false,
};

const ScheduleOnlyOptionsModal = ({
	clientId,
	options,
	show,
	onClose,
	onSave,
	onAddContact,
	schedulyOnlyOptions,
	enforceV2NonRKPayInvoicing
}) => {
	const { lang } = global;
	const [form, setForm] = useState(defaultScheduleOnlyOptions);
	const [clientSchedulingOnlyOptions, setClientSchedulingOnlyOptions] = useState(
		defaultScheduleOnlyOptions,
	);
	const [buttonState, setButtonState] = useState("");
	const [validations, setValidations] = useState(defaultValidations);
	const [isLoading, setIsLoading] = useState(true);

	const validateClientWillNotBePayingOptions = validateForm =>
		!isEmpty(`${validateForm.SchedulingJobType?.toString() || ""}`);

	const validateClientWillNotBeBilledOptions = validateForm =>
		!isEmpty(validateForm.PaymentDetails?.trim()) &&
		!isEmpty(`${validateForm.SchedulingBillingContactId || ""}`) &&
		!isEmpty(validateForm.PaymentMethod) &&
		!isEmpty(validateForm.NotInvoicingInformation?.trim());

	const updateFormForClientEnforcedV2 = (formData) => (
		// When a client enforces V2 we force to client not paying for the job.
		{
			...formData,
			ClientPaysForTheJob: false,
			ElectronicallyBilled: null,
			PaymentDetails: "",
			PaymentMethod: null,
			SchedulingBillingContactId: null,
			NotInvoicingInformation: ""
		}
	)

	const loadOptions = async () => {
		try {
			setIsLoading(true);

			if (clientId === undefined) {
				const inMemoryForm = schedulyOnlyOptions || defaultScheduleOnlyOptions;
				setForm(inMemoryForm);

				return;
			}

			const { data } = await JobService.getSchedulingOnlyOptions(clientId);

			if (isEmpty(data) && !enforceV2NonRKPayInvoicing) return;

			let formData = data;

			if (enforceV2NonRKPayInvoicing)
				formData = updateFormForClientEnforcedV2(data);

			setForm(formData);
			setClientSchedulingOnlyOptions(formData);
			setButtonState("");
		} catch {
			utils.notify({
				type: "error",
				message: lang.t("Error loading Scheduling Options"),
				timeOut: 1500,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleSave = async () => {
		try {
			setButtonState("loading");

			const onSuccess = async () => {
				if (clientId !== undefined) {
					await JobService.upsertSchedulingOnlyOptions({ ...form, ClientId: clientId });
				}

				onSave(form);
				onClose();
			};

			if (form.ClientPaysForTheJob === null && form.ElectronicallyBilled === null) return;

			if (!form.ClientPaysForTheJob && validateClientWillNotBePayingOptions(form)) {
				await onSuccess();
				return;
			}

			if (!form.ElectronicallyBilled && validateClientWillNotBeBilledOptions(form)) {
				await onSuccess();
				return;
			}

			setValidations({
				clientWillNotBePaying: !form.ClientPaysForTheJob,
				clientWillNotBeBilled: !form.ElectronicallyBilled,
			});
		} catch {
			setButtonState("error");
			utils.notify({
				type: "error",
				message: lang.t("Error saving Scheduling Options"),
				timeOut: 1500,
			});
		} finally {
			setButtonState("");
		}
	};

	const onValueChange = (
		payload,
		clientWillNotBePaying = false,
		clientWillNotBeBilled = false,
	) => {
		const newForm = { ...payload };

		if (clientWillNotBePaying) {
			newForm.PaymentDetails = "";
			newForm.PaymentMethod = null;
			newForm.SchedulingBillingContactId = "";
			newForm.NotInvoicingInformation = "";
		}

		if (clientWillNotBeBilled) {
			newForm.SchedulingJobType = null;
			newForm.AdditionalInformation = "";
		}

		setForm(prevForm => ({ ...prevForm, ...newForm }));
	};

	const handleClientPaysForTheJobChange = value =>
		setForm(prevForm => ({
			...prevForm,
			...defaultOptions,
			...clientSchedulingOnlyOptions,
			ClientPaysForTheJob: value,
			ElectronicallyBilled: value ? false : null,
		}));

	useEffect(() => {
		if (!show) {
			setValidations(defaultValidations);
			setForm(defaultScheduleOnlyOptions);

			return;
		}

		loadOptions();
	}, [show]);

	return (
		<Modal
			size="md"
			show={show}
			onHide={onClose}
		>
			<Modal.Header closeButton>
				<Modal.Title>{lang.t("Scheduling Only Defaults")}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<Choose>
					<When condition={isLoading}>
						<Loader
							active={isLoading}
							size="small"
						/>
					</When>
					<Otherwise>
						<div className="scheduling-only-options__modal">
							<If condition={form.ClientPaysForTheJob === null}>
								<div className="row">
									<div className="col-xs-12">
										{lang.t("Is a client going to be paying for this job_")}
									</div>
								</div>
								<div className="row">
									<div className="col-xs-4 col-sm-2 radio">
										<label htmlFor="clientPaysForTheJob_yes">
											<input
												type="radio"
												name="clientPaysForTheJob"
												id="clientPaysForTheJob_yes"
												onChange={() =>
													handleClientPaysForTheJobChange(true)
												}
											/>
											{lang.t("Yes")}
										</label>
									</div>
									<div className="col-xs-4 col-sm-2 radio">
										<label htmlFor="clientPaysForTheJob_no">
											<input
												type="radio"
												name="clientPaysForTheJob"
												id="clientPaysForTheJob_no"
												onChange={() =>
													handleClientPaysForTheJobChange(false)
												}
											/>
											{lang.t("No")}
										</label>
									</div>
								</div>
							</If>

							<If condition={form.ClientPaysForTheJob !== null}>
								<div className="row">
									<div className="col-xs-12 col-sm-10 form-group slideInUp">
										<ReactSelect
											id="client-paying-id"
											name="clientPaysForTheJob"
											value={form.ClientPaysForTheJob}
											onChange={e =>
												handleClientPaysForTheJobChange(e?.value)
											}
											options={CLIENTS_PAYS_FOR_THE_JOB_OPTIONS}
											isDisabled={form.RequirePrepayment || enforceV2NonRKPayInvoicing}
											components={{ Control, Option }}
										/>
									</div>
								</div>

								<If condition={form.ClientPaysForTheJob === false}>
									<div className="row form-group">
										<div
											className={classNames("col-xs-12", {
												"invalid-field":
													validations.clientWillNotBePaying &&
													isEmpty(
														`${form.SchedulingJobType?.toString() || ""}`,
													),
											})}
										>
											<label>
												{lang.t("What kind of job is this_")}
												<span className="required-mark" />
											</label>
										</div>
										<div className="col-xs-12 radio">
											<label htmlFor="ondutyshift">
												<input
													type="radio"
													name="JobType"
													id="ondutyshift"
													value={ADDITIONAL_JOB_TYPES.ON_DUTY_SHIFT}
													checked={
														form.SchedulingJobType ===
														ADDITIONAL_JOB_TYPES.ON_DUTY_SHIFT
													}
													onChange={({ target: { value } }) =>
														onValueChange(
															{
																SchedulingJobType: Number(value),
																AdditionalInformation: "",
															},
															true,
															false,
														)
													}
												/>
												{lang.t("On-duty Shift")}
											</label>
										</div>
										<div className="col-xs-12 radio">
											<label htmlFor="nonprofit">
												<input
													type="radio"
													name="JobType"
													id="nonprofit"
													value={ADDITIONAL_JOB_TYPES.NON_PROFIT}
													checked={
														form.SchedulingJobType ===
														ADDITIONAL_JOB_TYPES.NON_PROFIT
													}
													onChange={({ target: { value } }) =>
														onValueChange(
															{
																SchedulingJobType: Number(value),
																AdditionalInformation: "",
															},
															true,
															false,
														)
													}
												/>
												{lang.t("Non Profit")}
											</label>
										</div>
										<div className="col-xs-12 radio">
											<label htmlFor="grant">
												<input
													type="radio"
													name="JobType"
													id="grant"
													value={ADDITIONAL_JOB_TYPES.GRANT}
													checked={
														form.SchedulingJobType ===
														ADDITIONAL_JOB_TYPES.GRANT
													}
													onChange={({ target: { value } }) =>
														onValueChange(
															{
																SchedulingJobType: Number(value),
																AdditionalInformation: "",
															},
															true,
															false,
														)
													}
												/>
												{lang.t("Grant")}
											</label>
										</div>
										<div className="col-xs-12 radio">
											<label htmlFor="overtime">
												<input
													type="radio"
													name="JobType"
													id="overtime"
													value={ADDITIONAL_JOB_TYPES.OVERTIME}
													checked={
														form.SchedulingJobType ===
														ADDITIONAL_JOB_TYPES.OVERTIME
													}
													onChange={({ target: { value } }) =>
														onValueChange(
															{
																SchedulingJobType: Number(value),
																AdditionalInformation: "",
															},
															true,
															false,
														)
													}
												/>
												{lang.t("Overtime")}
											</label>
										</div>
										<div className="col-xs-12 radio">
											<label htmlFor="other">
												<input
													type="radio"
													name="JobType"
													id="other"
													value={ADDITIONAL_JOB_TYPES.OTHER}
													checked={
														form.SchedulingJobType ===
														ADDITIONAL_JOB_TYPES.OTHER
													}
													onChange={({ target: { value } }) =>
														onValueChange(
															{
																SchedulingJobType: Number(value),
																AdditionalInformation: "",
															},
															true,
															false,
														)
													}
												/>
												{lang.t("Other")}
											</label>
										</div>
										<If
											condition={
												form.SchedulingJobType ===
												ADDITIONAL_JOB_TYPES.OTHER
											}
										>
											<div className="form-group padding-left-15 padding-right-15">
												<textarea
													id="OtherDetails"
													name="OtherDetails"
													className="form-control"
													placeholder={lang.t("Please provide details")}
													value={form.AdditionalInformation}
													maxLength={500}
													onBlur={() =>
														onValueChange({
															AdditionalInformation:
																form.AdditionalInformation.trim(),
														})
													}
													onChange={({ target: { value } }) =>
														onValueChange(
															{ AdditionalInformation: value },
															true,
															false,
														)
													}
												/>
											</div>
										</If>
									</div>
								</If>
							</If>

							<If condition={form.ElectronicallyBilled != null}>
								<div className="row">
									<div className="col-xs-12 col-sm-12 form-group slideInUp">
										<ReactSelect
											id="billed-electronically-id"
											name="electronicallyBilled"
											value={form.ElectronicallyBilled}
											options={ELECTRONICALLY_BILLED_OPTIONS}
											isDisabled
											components={{ Control, Option }}
										/>
									</div>
								</div>

								<If condition={form.ElectronicallyBilled === false}>
									<div className="row form-group">
										<div
											className={classNames("col-xs-12", {
												"invalid-field":
													validations.clientWillNotBeBilled &&
													isEmpty(form.PaymentDetails?.trim()),
											})}
										>
											<label className="control-label">
												{lang.t("Payment Details")}
												<span className="required-mark" />
											</label>
											<textarea
												name="paymentDetails"
												className="form-control"
												value={form.PaymentDetails}
												maxLength={500}
												onBlur={() =>
													onValueChange({
														PaymentDetails: form.PaymentDetails.trim(),
													})
												}
												onChange={({ target: { value } }) =>
													onValueChange(
														{ PaymentDetails: value },
														false,
														true,
													)
												}
											/>
										</div>
									</div>

									<div className="row form-group">
										<div
											className={classNames("col-xs-12", {
												"invalid-field":
													validations.clientWillNotBeBilled &&
													isEmpty(
														`${form.SchedulingBillingContactId || ""}`,
													),
											})}
										>
											<div className="col-xs-6 padding-left-0">
												<label
													className="control-label"
													htmlFor="BillingContactIdModal"
												>
													{lang.t("Billing Contact")}
													<span className="required-mark" />
												</label>
											</div>
											<div className="col-xs-6 text-right padding-right-0">
												<button
													type="button"
													className="btn btn-link open-add-contact-modal btn-blue-small"
													onClick={onAddContact}
													data-test-id="jobs__open-add-contact-modal"
												>
													+ {lang.t("New")}
												</button>
											</div>
										</div>
										<div
											className={classNames("col-xs-12", {
												"invalid-field":
													validations.clientWillNotBeBilled &&
													isEmpty(
														`${form.SchedulingBillingContactId || ""}`,
													),
											})}
										>
											<ReactSelect
												value={form.SchedulingBillingContactId}
												name="BillingContactIdModal"
												onChange={({ value }) =>
													onValueChange(
														{ SchedulingBillingContactId: value },
														false,
														true,
													)
												}
												options={options}
											/>
										</div>
									</div>

									<div className="row form-group">
										<div
											className={classNames("col-xs-12", {
												"invalid-field":
													validations.clientWillNotBeBilled &&
													isEmpty(form.PaymentMethod),
											})}
										>
											<label className="control-label">
												{lang.t("How will this job get paid_")}
												<span className="required-mark" />
											</label>
										</div>
										<div className="col-xs-12 radio">
											<label>
												<input
													type="radio"
													name="HowJobGetPaid"
													value={JOB_PAYMENT_TYPES.CASH.value}
													checked={
														form.PaymentMethod ===
														JOB_PAYMENT_TYPES.CASH.value
													}
													onChange={({ target: { value } }) =>
														onValueChange(
															{ PaymentMethod: value },
															false,
															true,
														)
													}
												/>
												{JOB_PAYMENT_TYPES.CASH.label}
											</label>
										</div>
										<div className="col-xs-12 radio">
											<label>
												<input
													type="radio"
													name="HowJobGetPaid"
													value={JOB_PAYMENT_TYPES.CHECK.value}
													checked={
														form.PaymentMethod ===
														JOB_PAYMENT_TYPES.CHECK.value
													}
													onChange={({ target: { value } }) =>
														onValueChange(
															{ PaymentMethod: value },
															false,
															true,
														)
													}
												/>
												{JOB_PAYMENT_TYPES.CHECK.label}
											</label>
										</div>
										<div className="col-xs-12 radio">
											<label>
												<input
													type="radio"
													name="HowJobGetPaid"
													value={JOB_PAYMENT_TYPES.OTHER.value}
													checked={
														form.PaymentMethod ===
														JOB_PAYMENT_TYPES.OTHER.value
													}
													onChange={({ target: { value } }) =>
														onValueChange(
															{ PaymentMethod: value },
															false,
															true,
														)
													}
												/>
												{JOB_PAYMENT_TYPES.OTHER.label}
											</label>
										</div>
									</div>

									<div className="row form-group">
										<div
											className={classNames("col-xs-12", {
												"invalid-field":
													validations.clientWillNotBeBilled &&
													isEmpty(form.NotInvoicingInformation?.trim()),
											})}
										>
											<label className="control-label">
												{lang.t(
													"Please share the reason for not utilizing the RollKall platform for online invoicing_",
												)}
												<span className="required-mark" />
											</label>
											<textarea
												name="notRollkallDetails"
												className="form-control"
												value={form.NotInvoicingInformation}
												maxLength={500}
												onBlur={() =>
													onValueChange({
														NotInvoicingInformation:
															form.NotInvoicingInformation.trim(),
													})
												}
												onChange={({ target: { value } }) =>
													onValueChange(
														{ NotInvoicingInformation: value },
														false,
														true,
													)
												}
											/>
										</div>
									</div>
								</If>
							</If>
						</div>
					</Otherwise>
				</Choose>
			</Modal.Body>

			<Modal.Footer>
				<button
					type="button"
					className="btn btn-link"
					onClick={onClose}
					disabled={isLoading}
				>
					{lang.t("Cancel")}
				</button>
				<ProgressButton
					id="add-client"
					className="btn btn-primary"
					state={isLoading ? "disabled" : buttonState}
					onClick={handleSave}
				>
					{lang.t("Save")}
				</ProgressButton>
			</Modal.Footer>
		</Modal>
	);
};

ScheduleOnlyOptionsModal.propTypes = {
	clientId: PropTypes.number.isRequired,
	show: PropTypes.bool,
	onClose: PropTypes.func,
	onSave: PropTypes.func,
	onAddContact: PropTypes.func.isRequired,
	options: PropTypes.array,
	schedulyOnlyOptions: PropTypes.object,
	enforceV2NonRKPayInvoicing: PropTypes.bool,
};

ScheduleOnlyOptionsModal.defaultProps = {
	show: false,
	options: [],
	onSave: () => {},
	onClose: () => {},
	schedulyOnlyOptions: null,
	enforceV2NonRKPayInvoicing: false
};

export default ScheduleOnlyOptionsModal;
